import { createReportWithoutVictim, type SymmetricKey } from '@faceup/crypto'
import type { Attachment } from '@faceup/ui'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'

const fragments = {
  usePrepareReportPayloadFragments_system: graphql(`
    fragment usePrepareReportPayloadFragments_system on System {
      id
      publicKey
    }
  `),
  usePrepareReportPayloadFragments_member: graphql(`
    fragment usePrepareReportPayloadFragments_member on Member {
      id
      keys {
        id
        publicKey
      }
    }
  `),
}

/**
 * If a member creates or imports a report in an organizational unit they don't have access to, and we want
 * to make the report visible to the user, we can pass _creator with their public key.
 */
export const usePrepareReportPayload =
  () =>
  async (
    _recipients: FragmentType<typeof fragments.usePrepareReportPayloadFragments_member>[],
    _system: FragmentType<typeof fragments.usePrepareReportPayloadFragments_system>,
    _creator: FragmentType<typeof fragments.usePrepareReportPayloadFragments_member> | null,
    isE2EE: boolean,
    values: {
      victimName: string | null
      moreInfo: string
      moreInfoAttachments?: Attachment[]
      attachments?: Attachment[]
    },
    // Symmetric key used for encryption and will be wrapped for the recipients; omit to generate a new one.
    reportKey?: SymmetricKey
  ) => {
    const recipients = getFragmentData(
      fragments.usePrepareReportPayloadFragments_member,
      _recipients
    )
    const system = getFragmentData(fragments.usePrepareReportPayloadFragments_system, _system)
    const creator = getFragmentData(fragments.usePrepareReportPayloadFragments_member, _creator)

    const systemPublicKey = system.publicKey

    const recipientsKeys =
      recipients?.map(recipient => ({
        id: recipient?.id ?? '',
        key: recipient?.keys?.publicKey ?? '',
      })) ?? []

    // make sure that we don't create duplicates in the recipientsKeys when adding the member
    if (creator && !recipientsKeys.some(({ id }) => id === creator.id)) {
      recipientsKeys.push({
        id: creator.id,
        key: creator.keys?.publicKey ?? '',
      })
    }

    return await createReportWithoutVictim(
      {
        victimName: values.victimName,
        moreInfo: values.moreInfo.trim(),
        attachments: values.attachments?.map(({ file }) => file),
        moreInfoAttachments: values.moreInfoAttachments?.map(({ file }) => file),
      },
      recipientsKeys,
      reportKey,
      isE2EE ? undefined : systemPublicKey
    )
  }
